---
title: "Year-end promotion"
slug: "year-end-promotion"
category: "borrowers"
description: "Move every pupil up a year group, graduate leavers, and tidy the database in one pass."
tags: ["year end", "promotion", "leavers", "annual", "july", "september"]
order: 3
---

Every July, every school library has the same job: bump every pupil up a year group, remove the leavers, and reset a few counters so September starts cleanly. BookBurrow has a single wizard that does all of this in about two minutes. No CSV re-import, no deleting records by hand.

## When to run it

Run the year-end wizard **after** pupils have returned their books and **before** the first day of the new school year. Most schools do it on the last day of the summer term, or in the last week of the holidays.

Running it twice is fine — the wizard detects if year-end has already been done and won't re-promote anyone. Running it at the wrong moment (say, in February) will also refuse unless you explicitly confirm.

## What the wizard does

When you run year-end, BookBurrow will:

1. **Promote every pupil** up a year group (Year 3 becomes Year 4, Year 4 becomes Year 5, and so on).
2. **Graduate leavers** — Year 6 (primary) or Year 11 / 13 (secondary) pupils are marked as leavers and archived.
3. **Clear outstanding loans** optionally, if you confirm that any books the leavers still have are lost or written off.
4. **Purge old loan history** according to your retention settings (default: keep the current academic year, archive everything older).
5. **Reset annual counters** like "books borrowed this year" on every borrower.
6. **Generate a summary report** with leaver lists, loan statistics for the year, and any outstanding issues.

<Callout variant="warning" title="Take a backup first">
The wizard is safe and reversible within 48 hours, but do yourself a favour and take a manual backup before running it. **File → Backup now** — it takes five seconds and gives you an absolute rollback point if anything surprises you.
</Callout>

## Running the wizard

<Steps>
  <Step title="Open Settings → Year end">
    You'll need the admin PIN.
  </Step>
  <Step title="Review the summary">
    BookBurrow shows you: total pupils currently in each year group, how many will be promoted, how many leavers, and how many outstanding loans from leavers.
  </Step>
  <Step title="Deal with outstanding loans from leavers">
    For each leaver with books still on loan, pick one of three options: **Returned** (the book came back after all), **Lost** (write off), or **Keep on record** (rare, but useful if the book is about to be returned).
  </Step>
  <Step title="Confirm and run">
    The wizard takes maybe fifteen seconds to process everything, even for a thousand pupils.
  </Step>
  <Step title="Save the summary report">
    You'll be offered a PDF summary — print it for your head teacher, attach it to the library annual review, or just archive it.
  </Step>
</Steps>

## What about new starters for September?

Year-end doesn't add new starters, because you usually don't know the full new list until September. The normal flow is:

1. Run year-end at the end of July (promotes existing pupils, graduates leavers).
2. In early September, **[import a fresh CSV](/help/guides/importing-from-sims-arbor-bromcom)** from your MIS that contains the whole new school population.
3. BookBurrow's importer skips pupils who are already in the system, and adds only the new Year 7 / Reception cohort.

This way, the new intake appears in one batch and old pupils aren't duplicated. It's also the right moment to [print borrower cards for the new intake](/help/guides/printing-borrower-cards) so they can start using the library on day one.

For the short version of this workflow, see the [year-end FAQ entry](/help/faq#year-end).

## Undoing year-end

If you run the wizard by mistake, **Settings → Year end → Undo last run** rolls back the promotions and restores leavers. This only works within 48 hours and only if you haven't done any loans since. After that window, you'd have to restore from a backup — which is one more reason to take one first.
